import { useState, useEffect, useRef } from 'react'

export interface OnlineStatus {
  /** Whether the browser currently has network connectivity */
  isOnline: boolean
  /** True after the first reconnection — reset to false after a delay */
  wasOffline: boolean
}

/**
 * Tracks the browser's online/offline status.
 *
 * Returns:
 *  - `isOnline`  — live value of `navigator.onLine`
 *  - `wasOffline` — becomes true when the connection is restored after having
 *    been offline.  Stays true for 5 seconds so consumers can trigger a sync.
 */
export function useOnlineStatus(): OnlineStatus {
  const [isOnline, setIsOnline] = useState<boolean>(() => navigator.onLine)
  const [wasOffline, setWasOffline] = useState<boolean>(false)
  // Track whether we have ever been offline during this session
  const wentOffline = useRef<boolean>(false)
  const wasOfflineTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true)
      if (wentOffline.current) {
        // We just came back from offline — signal sync opportunity
        setWasOffline(true)
        wentOffline.current = false
        // Auto-clear after 5 seconds
        if (wasOfflineTimer.current !== null) {
          clearTimeout(wasOfflineTimer.current)
        }
        wasOfflineTimer.current = setTimeout(() => {
          setWasOffline(false)
          wasOfflineTimer.current = null
        }, 5_000)
      }
    }

    const handleOffline = () => {
      setIsOnline(false)
      wentOffline.current = true
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      if (wasOfflineTimer.current !== null) {
        clearTimeout(wasOfflineTimer.current)
      }
    }
  }, [])

  return { isOnline, wasOffline }
}
